// Pre-compiled regexes at module level — compiled once, not on each matchRoute call
const REGEX_PUBLISH_DETAIL = /\/[a-zA-Z0-9_+-]+\/[a-zA-Z0-9_+-]+-\d+\/$/;
const REGEX_SLUG_ID_REPLACE = /\/([a-zA-Z0-9_+-]+-\d+)\/$/;
const REGEX_NEWSLETTER = /\d+-[a-zA-Z0-9_+-]+\/$/;

export function useApiRoutes(opts = {}) {
	const nuxtApp = useNuxtApp();
	const lang = useLang();
	const {getUrlSegments} = useUrl();
	const {getCatalogLangBasePath} = useCatalog({route: opts.route});
	const appConfig = useAppConfig();

	function routeKeys(route) {
		const template = route.cms_data?.template || route.template;
		const keys = [route.app_url, route.cms_data?.app_url];
		if (template) {
			const key = template.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[/_-]+/g, '_').toLowerCase().replace(/_index$/, '');
			keys.push(key);
			if (key.startsWith('cms_')) keys.push(key.slice(4));
		}
		return keys.filter(Boolean);
	}

	function routeParamNames(route) {
		if (route?.params) return route.params;
		if (!route?.template && !route?.cms_data?.template) return [];
		return route.path?.split('/').map(segment => segment.match(/^%(\w+)%$/)?.[1]).filter(Boolean) || [];
	}

	function getRouteParams(route, path) {
		const names = routeParamNames(route);
		if (!names.length || !path) return null;
		const pattern = route.path.split('/').filter(Boolean);
		const segments = path.split('/').filter(Boolean);
		if (pattern.length !== segments.length) return null;
		const params = {};
		for (let index = 0; index < pattern.length; index++) {
			const name = names.find(name => pattern[index] === `%${name}%`);
			if (!name) {
				if (pattern[index] !== segments[index]) return null;
				continue;
			}
			try {
				const value = decodeURIComponent(segments[index]);
				if (!value || /[\/\\]/.test(value) || ['.', '..'].includes(value)) return null;
				params[name] = value;
			} catch {
				return null;
			}
		}
		return params;
	}

	// loop through routes and set global appUrls
	function processRoutes(data) {
		const urls = {
			home: {url: '/'},
			catalog: {url: ''},
			compare: {url: ''},
			wishlist: {url: ''},
			manufacturer: {url: ''},
			search: {url: ''},
			location: {url: ''},
		};
		data.forEach(routeItem => {
			const routeSegments = getUrlSegments(routeItem.path, {ignoreLang: true});

			// set home url
			if (routeItem.controller == 'cms' && (routeItem.template == 'cms/homepage' || routeItem.path == `/${lang.get()}/`)) {
				urls['home'] = {url: routeItem.path == '//' ? '/' : routeItem.path};
			}

			// set catalog url
			if (routeItem.controller == 'catalog' && routeItem.action == 'index' && !routeItem.urls) {
				if (routeSegments.length == 1) {
					urls['catalog'] = {url: routeItem.path};
					urls['catalog_quickorder'] = {url: routeItem.path + '?special_view=order_form'};
				}
			}

			if (routeItem.controller == 'catalog' && routeItem.action == 'index_manufacturer') {
				urls['manufacturer'] = {url: routeItem.path};
			}

			// set compare url
			if (routeItem.controller == 'cms' && routeItem.template == 'catalog/compare') {
				urls['compare'] = {url: routeItem.path};
			}

			// set wishlist url
			if (routeItem.controller == 'catalog' && routeItem.action == 'wishlist') {
				urls['wishlist'] = {url: routeItem.path};
			}

			// set sweepstake url
			if (routeItem.controller == 'sweepstake') {
				urls['sweepstake'] = {url: routeItem.path};
			}

			// set location url
			if (routeItem.controller == 'location') {
				urls['location'] = {url: routeItem.path};
			}

			// set search url
			if (routeItem.controller == 'cms' && routeItem.content_type == 'search') {
				const r = routeItem.path.split('/').filter(el => el !== '');
				if (routeSegments.length == 1) urls['search'] = {url: routeItem.path};
			}

			// set webshop urls
			if (routeItem.controller == 'webshop') {
				urls['webshop_' + routeItem.action] = {url: routeItem.path};
			}

			// set auth urls
			if (routeItem.controller == 'cms' && routeItem.content_type == 'auth') {
				if (routeSegments.length == 1) {
					urls['auth'] = {url: routeItem.path};
					urls['auth_logout'] = {url: routeItem.path + 'logout/'};
				}

				// if routeSegments length is more than 1, remove first element and add to appUrls
				if (routeSegments.length > 1) {
					routeSegments.shift();
					const url = routeSegments.join('_');
					urls['auth_' + url] = {url: routeItem.path};
				}
			}
		});

		const publishRoots = data.filter(route => route.controller === 'publish'
			&& route.action === 'index' && route.content_type === 'category' && !route.cms_data
			&& getUrlSegments(route.path, {ignoreLang: true}).length === 1);
		if (publishRoots.length === 1) urls.publish = {url: publishRoots[0].path};

		nuxtApp.$appData.appUrls = urls;
		return urls;
	}

	function isNumber(str) {
		return /^\d+$/.test(str);
	}

	// convert routes object received from API to simple array
	function remapRoutes(routes) {
		return Object.entries(routes).map(el => {
			return {
				path: el[0],
				...el[1],
			};
		});
	}

	// match current url with api routes
	function matchRoute(routes, currentPath) {
		routes = routes ?? get();
		if (routes.some(route => route.path === currentPath)) return currentPath;
		const parameterizedRoute = routes.find(route => getRouteParams(route, currentPath));
		if (parameterizedRoute) return parameterizedRoute.path;

		const removeLast = __FILTER_URL_TYPE__ == 3 && currentPath.includes(':');
		let path = getUrlSegments(currentPath, {stringify: true, addSlashes: true, removeLast: removeLast});

		let url = '';
		const urlParts = path.replace(/\//g, '').split('-'); // separate url parts by dash
		const last = urlParts[urlParts.length - 1];
		const secondLast = urlParts[urlParts.length - 2];
		const routeSegments = getUrlSegments(path, {ignoreLang: true});
		const firstSegment = routeSegments[0];
		const secondSegment = routeSegments[1];
		const isDefaultLang = lang.isDefaultLang();
		const currentLang = lang.get();
		const langCode = isDefaultLang ? '' : `/${currentLang}`;
		const catalogBasePath = getCatalogLangBasePath(currentLang);
		const routesMap = nuxtApp.$appData.routesMap;
		const lastIsNumber = isNumber(last);

		// homepage api route is '//' so we need to update path to match it
		if (path == '/') path = '//';

		// cached stripped path
		const strippedPath = path.replace(/^\/|\/$/g, '');
		const strippedCurrentPath = currentPath.replace(/^\/|\/$/g, '');

		// match current route
		if (routesMap?.has(path)) return path;

		// match view order
		if (routeSegments.length == 3 && firstSegment == 'webshop' && ['orders', 'narocila', 'narudzbe', 'bestellen'].includes(secondSegment)) {
			return `${langCode}/${firstSegment}/${secondSegment}/`;
		}

		// match tags
		if (routeSegments.length == 2 && ['tagovi', 'tags', 'tagovi-proizvoda', 'product-tags', 'produkt-tags'].includes(firstSegment)) return `${langCode}/${firstSegment}/%SLUG%-%ID%/`;

		// match catalog detail
		if (__PRODUCT_URL_TYPE__ == 2) {
			// Url example: /proizvodi/category1/category2/lorem-ipsum-dolor-27436/
			if (routeSegments.length > 2 && firstSegment == catalogBasePath.items && lastIsNumber) return `${langCode}/${catalogBasePath.items}/%URL%/%SLUG%-%ID%/`;
		} else {
			// Url example: /lorem-ipsum-dolor-proizvod-27436/
			if (lastIsNumber && secondLast == catalogBasePath.item) return `${langCode}/%SLUG%-${catalogBasePath.item}-%ID%/`;
		}

		if (__CATALOG_SEO_URL__ && lastIsNumber && routeSegments.length > 2) {
			// if catalog seo urls are enabled, also match old urls indexed by google
			if (firstSegment == catalogBasePath.items) return `${langCode}/%SLUG%-${catalogBasePath.item}-%ID%/`;
		}

		// match publish detail
		if (REGEX_PUBLISH_DETAIL.test(path) && !['proizvodi', 'izdelki', 'products', 'produkte', 'producto'].includes(firstSegment)) {
			if (__CATEGORY_WITHOUT_BASE_URL__) {
				// Check if this is a catalog category url
				const categoryRoute = routesMap?.get(`${langCode}/%CATALOG_CATEGORY_SLUG%/`);
				const isCatalogCategory = categoryRoute?.urlsSet?.has(strippedPath);
				if (!isCatalogCategory) return path.replace(REGEX_SLUG_ID_REPLACE, '/%SLUG%-%ID%/');
			} else {
				return path.replace(REGEX_SLUG_ID_REPLACE, '/%SLUG%-%ID%/');
			}
		}

		// Match author detail
		if (__PUBLISH_AUTHORS__ && routeSegments.length <= 2 && ['autori', 'authors'].includes(firstSegment)) return `${langCode}/${firstSegment}/%SLUG%/`;

		// match newsletter subscribe
		if (__NEWSLETTER__) {
			if (routeSegments.length === 3 && firstSegment == 'newsletter' && REGEX_NEWSLETTER.test(path)) return path.replace(REGEX_NEWSLETTER, '%ID%-%CODE%/');
		}

		// match brand detail
		if (routeSegments.length === 3) {
			const limitUrlSegments = isDefaultLang ? 2 : 3;
			url = getUrlSegments(path, {limit: limitUrlSegments, append: '%SLUG%', stringify: true, addSlashes: true});
			if (routesMap?.has(url)) return url;
		}

		// match manufacturer + category URL: /brand-base/manufacturers/%SLUG%/%URL% (no trailing slash in backend route key)
		if (__MANUFACTURER_CATEGORY_URLS__ && routeSegments.length >= 4) {
			const limitUrlSegments = isDefaultLang ? 2 : 3;
			const basePath = getUrlSegments(path, {limit: limitUrlSegments, stringify: true, addSlashes: true});
			const mfrCategoryKey = `${basePath}%SLUG%/%URL%`;
			if (routesMap?.has(mfrCategoryKey)) return mfrCategoryKey;
		}

		// match catalog category url format without prefix (/category-url-<ID>/) or with prefix (/proizvodi/category-url-<ID>/)
		if (__CATALOG_SEO_URL__) {
			if (routeSegments.length === 1 && firstSegment.match(/^(.+)-(\d+)$/)) {
				const categoryPath = `${langCode}/%CATSLUG%-%ID%/`;
				const manufacturerPath = `${langCode}/%MANSLUG%-%ID%/`;

				const categoryRoute = routesMap?.get(categoryPath);
				const manufacturerRoute = routesMap?.get(manufacturerPath);

				if (categoryRoute?.urlsSet?.has(strippedCurrentPath)) return categoryPath;
				if (manufacturerRoute?.urlsSet?.has(strippedCurrentPath)) return manufacturerPath;
				return categoryPath;
			}
		} else if (__CATEGORY_WITHOUT_BASE_URL__) {
			// Find if current url is defined in %CATALOG_CATEGORY_SLUG% route urls. If so, return category path.
			url = `${langCode}/%CATALOG_CATEGORY_SLUG%/`;
			const categoryRoutes = routesMap?.get(url);
			const categoryPath = getUrlSegments(path, {ignoreLang: true, stringify: true});
			const categoryUrl = categoryRoutes?.urlsSet?.has(categoryPath);
			if (categoryUrl) return url;
		} else if (routeSegments.length > 1) {
			url = `${langCode}/${firstSegment}/%URL%/`;
			if (routesMap?.has(url)) return url;
		}

		// Match payment page (eg. KEKS pay)
		if (['payment-create-quick', 'payment-canceled-quick'].includes(secondSegment)) {
			url = getUrlSegments(path, {limit: 2, stringify: true, addSlashes: true});
			return `${langCode}${url}`;
		}

		// Match create order page (view order quick payment link)
		if (routeSegments.length == 4 && getUrlSegments(path, {ignoreLang: true, limit: 3, stringify: true}) == 'webshop/order/payment') {
			return '/webshop/create-order/';
		}

		// if there is no match, try to match only first route param
		// skip fallback for webshop sub-URLs — excluded steps should return 404, not fall back to cart
		if (routeSegments.length > 1 && firstSegment !== 'webshop') {
			const limitUrlSegments = isDefaultLang ? 1 : 2;
			url = getUrlSegments(path, {limit: limitUrlSegments, stringify: true, addSlashes: true});
			if (routesMap?.has(url)) return url;
		}
	}

	// fetch all routes from api
	async function fetchRoutes() {
		try {
			const res = await useApi(`/api/nuxtapi/routes/?lang=${lang.get()}`);
			if (res.data) {
				setRoutes(res.data);
			}
			return res;
		} catch (err) {
			throw createError({
				statusCode: err.statusCode,
				statusMessage: `Error ${err.statusCode}`,
			});
		}
	}

	// filter only routes for current language
	function filterRoutes(routes) {
		const defaultLang = lang.get();
		const webshopSteps = appConfig.webshop?.steps;

		// Create a Set of excluded actions for O(1) lookup
		const excludedActions = new Set();
		if (webshopSteps?.length) {
			if (!webshopSteps.includes('shipping')) excludedActions.add('shipping');
			if (!webshopSteps.includes('payment')) excludedActions.add('payment');
			if (!webshopSteps.includes('reviewOrder')) excludedActions.add('review_order');
		}

		return routes.filter(route => {
			// Filter by language first
			if (route.lang !== defaultLang) return false;

			// Filter webshop routes if needed
			if (excludedActions.size > 0 && route.controller === 'webshop' && excludedActions.has(route.action)) {
				return false;
			}

			return true;
		});
	}

	// get all routes
	function get() {
		return nuxtApp.$appData?.routes ? nuxtApp.$appData.routes : [];
	}

	// get route from appRoutes.
	function getAppUrl(key, options = 'url') {
		let route;
		if (key) {
			for (const candidate of get()) {
				if (routeKeys(candidate).includes(key)) route = candidate;
			}
		}
		if (route) {
			const data = options && typeof options === 'object' ? options.data || 'url' : options;
			if (data !== 'url') return route[data] ?? null;
			let url = route.path;
			for (const name of routeParamNames(route)) {
				const value = options?.params?.[name];
				if (value === undefined || value === null || value === '' || /[\/\\]/.test(String(value)) || ['.', '..'].includes(String(value))) return null;
				url = url.replace(`%${name}%`, encodeURIComponent(value));
			}
			return url;
		}

		// Second param can be a string (legacy: data key, e.g. 'url') or an options object { data, fallback, log }
		const isOptionsObj = options && typeof options === 'object';
		const data = isOptionsObj ? options.data || 'url' : options;
		const opts = isOptionsObj ? options : {};

		if (!key || !nuxtApp.$appData.appUrls[key]) {
			const fallback = opts.fallback || 'home';
			if (import.meta.client && opts.log !== false) console.warn(`The "${key}" route does not exist. The "${fallback}" route is returned instead.`);
			return nuxtApp.$appData.appUrls[fallback][data] || null;
		}

		return nuxtApp.$appData.appUrls[key][data];
	}

	// get all app routes as a single object
	function getAppUrls() {
		let items = {};
		Object.entries(nuxtApp.$appData.appUrls).forEach(item => {
			items[item[0]] = item[1].url;
		});

		for (const route of get()) {
			if (!routeParamNames(route).length) {
				for (const key of routeKeys(route)) items[key] = route.path;
			}
		}

		return items;
	}

	function setRoutes(data) {
		const prepared = appConfig.hooks?.prepareRoutes ? appConfig.hooks.prepareRoutes(structuredClone(data)) : data;
		const routes = remapRoutes(prepared); // convert routes to simple array
		const filteredRoutes = filterRoutes(routes); // filter only routes for current language

		// pre-build Set and Map for O(1) lookups
		filteredRoutes.forEach(route => {
			if (Array.isArray(route.urls)) route.urlsSet = new Set(route.urls);
		});
		nuxtApp.$appData.routesMap = new Map(filteredRoutes.map(r => [r.path, r]));

		nuxtApp.$appData.routes = filteredRoutes; // set routes to appData
		processRoutes(filteredRoutes); // extract appUrls from routes
	}

	function getRoute(path) {
		return get().find(r => r.path == path) || get().find(route => getRouteParams(route, path));
	}

	return {routes: get(), fetchRoutes, get, getAppUrl, getAppUrls, setRoutes, processRoutes, matchRoute, remapRoutes, getRoute, getRouteParams};
}
